import { useEffect } from 'react';
import { isTextEntryTarget } from '@utils/keyboard';

interface UseReportPageShortcutsArgs {
  /** `G` — cycle the group-by token. */
  onCycleGroup: () => void;
  /** `E` — trigger the CSV export. */
  onExport: () => void;
  enabled?: boolean;
}

/**
 * Single-key shortcuts for the report run page (pattern: Nav.tsx Cmd+P
 * listener). Guards: never fire while typing, while a modifier is held, or
 * when the event was already handled (e.g. an open menu).
 */
export const useReportPageShortcuts = ({
  onCycleGroup,
  onExport,
  enabled = true,
}: UseReportPageShortcutsArgs): void => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;

      if (event.key === 'g' || event.key === 'G') {
        event.preventDefault();
        onCycleGroup();
      } else if (event.key === 'e' || event.key === 'E') {
        event.preventDefault();
        onExport();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCycleGroup, onExport, enabled]);
};
